// Line-pair DXF parser: reads the group-code / value pairs of a DXF file and
// materialises the entities we can render. Ported from the SimpleDXFParser in
// the monolith. Does NOT try to be a complete DXF reader — it handles the
// entity types the editor actually draws (LINE, ARC, CIRCLE, LWPOLYLINE,
// POLYLINE) plus the $INSUNITS header.

import type { RawEntity } from "../state/types.js";

export interface ParsedDxf {
  entities: RawEntity[];
  unitCode: number | null;
  // HEADER $CUSTOMPROPERTYTAG / $CUSTOMPROPERTY pairs, indexed by tag. Used to
  // detect DXF_HEALED (the fix_dxf pipeline stamps this on cleaned files) so a
  // re-uploaded healed file can skip the round-trip to Python.
  customVars: Record<string, string>;
}

const KNOWN_TYPES = new Set([
  "LINE",
  "ARC",
  "CIRCLE",
  "LWPOLYLINE",
  "POLYLINE",
]);

export function parseDxf(content: string): ParsedDxf {
  const lines = content.split(/\r?\n/);
  const entities: RawEntity[] = [];
  let current: RawEntity | null = null;

  // Old-style POLYLINE is a parent entity followed by a sequence of VERTEX
  // sub-entities terminated by SEQEND. We track the parent + the vertex we
  // are currently accumulating so codes 10/20 land on the vertex, not the
  // POLYLINE itself (whose own 10/20 is the elevation reference, not a point).
  let polyParent: RawEntity | null = null;
  let polyVertex: { x: number; y: number; b?: number } | null = null;

  const flushCurrent = () => {
    if (!current) return;
    if (KNOWN_TYPES.has(current.type)) entities.push(current);
    current = null;
  };

  const flushPoly = () => {
    if (!polyParent) return;
    if (polyVertex) {
      polyParent.vertices = polyParent.vertices ?? [];
      polyParent.vertices.push(polyVertex);
      polyVertex = null;
    }
    entities.push(polyParent);
    polyParent = null;
  };

  for (let i = 0; i < lines.length - 1; i += 2) {
    const codeRaw = lines[i]?.trim() ?? "";
    const valueRaw = lines[i + 1]?.trim() ?? "";
    if (codeRaw === "") continue;
    const code = Number.parseInt(codeRaw, 10);
    if (Number.isNaN(code)) continue;

    if (code === 0) {
      // Sub-entity handling for an active old-style POLYLINE.
      if (polyParent) {
        if (valueRaw === "VERTEX") {
          if (polyVertex) {
            polyParent.vertices = polyParent.vertices ?? [];
            polyParent.vertices.push(polyVertex);
          }
          polyVertex = { x: 0, y: 0 };
          continue;
        }
        if (valueRaw === "SEQEND") {
          flushPoly();
          continue;
        }
        // Any other boundary while inside a polyline — finalise it first,
        // then fall through to normal entity handling.
        flushPoly();
      }

      flushCurrent();
      if (valueRaw === "SECTION" || valueRaw === "ENDSEC" || valueRaw === "EOF") {
        continue;
      }

      if (valueRaw === "POLYLINE") {
        polyParent = { type: "POLYLINE", vertices: [] };
        polyVertex = null;
        continue;
      }

      current = { type: valueRaw as RawEntity["type"] };
      continue;
    }

    // Inside an old-style POLYLINE: route codes to the active vertex if any,
    // otherwise onto the parent (for layer, flags, etc).
    if (polyParent) {
      if (polyVertex) {
        if (code === 10) {
          const n = Number.parseFloat(valueRaw);
          if (!Number.isNaN(n)) polyVertex.x = n;
        } else if (code === 20) {
          const n = Number.parseFloat(valueRaw);
          if (!Number.isNaN(n)) polyVertex.y = n;
        } else if (code === 42) {
          // Bulge on this VERTEX sub-entity -- describes the arc from this
          // vertex to the next. Ignored means straight segment.
          const n = Number.parseFloat(valueRaw);
          if (!Number.isNaN(n)) polyVertex.b = n;
        }
        continue;
      }
      // POLYLINE header itself.
      if (code === 8) polyParent.layer = valueRaw;
      else if (code === 70) {
        const n = Number.parseFloat(valueRaw);
        if (!Number.isNaN(n)) polyParent.flags = n;
      }
      continue;
    }

    if (!current) continue;

    const num = Number.parseFloat(valueRaw);
    const isNum = !Number.isNaN(num);

    switch (code) {
      case 8:
        current.layer = valueRaw;
        break;
      case 10:
        if (isNum) {
          current.startX = num;
          if (current.type === "LWPOLYLINE") {
            current.vertices = current.vertices ?? [];
            current.vertices.push({ x: num, y: 0 });
          }
        }
        break;
      case 11:
        if (isNum) current.endX = num;
        break;
      case 20:
        if (isNum) {
          current.startY = num;
          if (current.type === "LWPOLYLINE" && current.vertices && current.vertices.length > 0) {
            current.vertices[current.vertices.length - 1]!.y = num;
          }
        }
        break;
      case 21:
        if (isNum) current.endY = num;
        break;
      case 40:
        if (isNum && (current.type === "CIRCLE" || current.type === "ARC")) {
          current.radius = num;
        }
        break;
      case 42:
        // LWPOLYLINE bulge for the LAST vertex we pushed. Describes the
        // arc from that vertex to the next; absent (or 0) = straight line.
        if (
          isNum &&
          current.type === "LWPOLYLINE" &&
          current.vertices &&
          current.vertices.length > 0
        ) {
          current.vertices[current.vertices.length - 1]!.b = num;
        }
        break;
      case 50:
        if (isNum) current.startAngle = num;
        break;
      case 51:
        if (isNum) current.endAngle = num;
        break;
      case 70:
        if (isNum) current.flags = num;
        break;
      case 90:
        if (isNum) current.vertexCount = num;
        break;
    }
  }

  flushPoly();
  flushCurrent();

  // Header scan for $INSUNITS. The header uses a "9\n$INSUNITS\n70\n<code>"
  // pattern; a regex over the raw text is more robust than reassembling from
  // the pair loop.
  let unitCode: number | null = null;
  const match = content.match(/\$INSUNITS\s+\d+\s+(\d+)/);
  if (match && match[1]) {
    const parsed = Number.parseInt(match[1], 10);
    if (!Number.isNaN(parsed)) unitCode = parsed;
  }

  // Header scan for custom vars. DXF encodes them as PAIRED entries where each
  // entry is a group-code line followed by its value line:
  //   9\n$CUSTOMPROPERTYTAG\n1\n<name>\n9\n$CUSTOMPROPERTY\n1\n<value>
  // The intermediate `9\n` between the tag capture and $CUSTOMPROPERTY is the
  // group-code line for the value's variable-name marker — the regex has to
  // step over it explicitly (an earlier version missed it and dropped every
  // custom var, which then broke auto-heal by letting the DXF_HEALED flag on
  // a cleaned file go undetected on re-load).
  const customVars: Record<string, string> = {};
  const cvPattern =
    /\$CUSTOMPROPERTYTAG\s+1\s+([^\r\n]+)\s+9\s+\$CUSTOMPROPERTY\s+1\s+([^\r\n]+)/g;
  for (const m of content.matchAll(cvPattern)) {
    const tag = m[1]?.trim();
    const value = m[2]?.trim();
    if (tag) customVars[tag] = value ?? "";
  }

  return { entities, unitCode, customVars };
}
